import { useQuery } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';
import useCurrentGameId from '@/shared/hooks/useCurrentGameId';
import { ClipGroupModel } from '../types';
import { ClipStatus } from '@/features/clips/types';
import { isUUID } from 'validator';

const ACTIVE_STATUSES: string[] = [
  ClipStatus.NEW,
  ClipStatus.INITIALIZING,
  ClipStatus.PENDING,
  ClipStatus.PROCESSING,
  ClipStatus.CANCELING,
];

export const getQueryKey = (gameId: string) => ['Games', gameId, 'ClipGroups'];

export const useGameClipGroupsQuery = (gameId?: string) => {
  const currentGameId = useCurrentGameId();
  const resolvedGameId = gameId || currentGameId;
  const isValidGame = !!resolvedGameId && isUUID(resolvedGameId);

  const query = useQuery({
    queryKey: getQueryKey(resolvedGameId),
    queryFn: async () => {
      if (!isValidGame) return [];
      const response = await get<{ success: boolean; clipGroups: ClipGroupModel[] }>(
        `/v1.0/games/${resolvedGameId}/clip-groups`
      );
      return response.data.clipGroups || [];
    },
    enabled: isValidGame,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchInterval: (query) => {
      const clipGroups = query.state.data;
      if (!clipGroups) return false;
      const hasActiveClips = clipGroups.some((group) =>
        group.clips?.some((clip: { status?: string }) =>
          clip.status && ACTIVE_STATUSES.includes(clip.status.toUpperCase())
        )
      );
      return hasActiveClips ? 3000 : false;
    },
  });

  return {
    ...query,
    clipGroups: query.data || [],
  };
};

export default useGameClipGroupsQuery;
